import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { RootStatePlatform } from "../../store/platform-slice";
import { useScoreSidePanel } from "../score/ScoreSidePanelContext";
import ScrollableWrapper from "../common/ScrollableWrapper";
import SecondaryTabMenuPicker from "../common/SecondaryTabMenuPicker";
import XIconSvg from "../../../../common/svgs/XIconSvg";
import BinSvg from "../../../../common/svgs/BinSvg";
import PlusSvg from "../../../../common/svgs/PlusSvg";
import RoundedIconContainer from "../common/RoundedIconContainer";
import ActionButton from "../../../../common/ui/ActionButton";
import LoadingSpinner from "../../../../common/ui/LoadingSpinner";
import AddNewBiomarkerWidget from "../biomarkerTab/AddNewBiomarkerWidget";
import CustomLineChart from "../score/CustomLineChart";
import { TimelineGroup } from "../score/SidePanelShared";
import BiomarkerRangeBar from "./BiomarkerRangeBar";
import TrendSparkline from "./TrendSparkline";
import {
  getAllEntries,
  getBiomarkerBadge,
  getBiomarkerPointerColor,
  getBiomarkerHatchColors,
  getChartEntries,
  getLatestEntry,
  getRangeBandColor,
  getRangeBounds,
  hasRecordedValue,
  hasValidRanges,
} from "./biomarkerDisplay";
import {
  BiomarkerItem,
  enPrecisionMetric,
} from "../../../../common/entities/type";
import {
  enPrecisionTestKind,
  formatBiomarkerTabValue,
} from "../../store/helper";
import { formatDisplayDate } from "../../../../common/utils/date";
import { isValidDate } from "../../../../providers/helperFunctions";
import { useAppStore } from "../../../../providers/AppStore";
import { useAuth } from "../../../../providers/AuthProvider";
import {
  DeleteCostumerBiomarker,
  UpdateCostumerBiomarker,
} from "../../../../common/network/Api";

/**
 * Health overview biomarker side panel (About / Readings).
 *
 * Rendered inside the score side panel drawer when contentType is
 * "userBiomarker". The panel resolves its biomarker live from
 * platform.biomarkerData by id, so it refreshes automatically after a
 * reading is added or deleted.
 *
 * The Readings CRUD (add value / delete) is affiliate-only — customers
 * (role 1) get a read-only list.
 */
const UserBiomarkerPanel = () => {
  const theme: Theme = useTheme();
  const { userBiomarkerData, closePanel } = useScoreSidePanel();
  const { AuthUser } = useAuth();
  const {
    ASGetClientBiomarkerTabDataHandler,
    ASGetDataFromPlatform,
    ASSetOpenSnackBar,
    ASSetSnackBarMessage,
    ASSetTypeSnackBar,
  } = useAppStore();

  const [activeTab, setActiveTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [value, setValue] = useState("");
  const [date, setDate] = useState<string | undefined>(undefined);

  const canEdit = AuthUser?.role !== 1;
  // Opened from faded sample-report content: the Readings tab stays visible
  // (consistent with the panel elsewhere) but is deactivated — clicking it
  // explains why via the shared snackbar instead of switching.
  const readingsLocked = Boolean(userBiomarkerData?.sampleContext);

  const biomarkerData = useSelector(
    (state: RootStatePlatform) => state.platform.biomarkerData
  );
  const biomarkerList = useSelector(
    (state: RootStatePlatform) => state.platform.biomarkerList
  );
  const systems = useSelector(
    (state: RootStatePlatform) =>
      state.platform.dataPlatformServer.score?.systems
  );
  const dashboard = useSelector(
    (state: RootStatePlatform) => state.platform.dataPlatformServer.dashboard
  );

  const item: BiomarkerItem | null = useMemo(() => {
    if (!userBiomarkerData) return null;
    for (const group of Object.values(biomarkerData || {})) {
      const found = (group as BiomarkerItem[]).find(
        (b) => b.biomarkerID === userBiomarkerData.biomarkerID
      );
      if (found) return found;
    }
    return null;
  }, [biomarkerData, userBiomarkerData]);

  // "Influenced by" / "Affects this" are AI-generated per report and ship on
  // the report payload (dashboard.bloodbiomarkers / breathbiomarkers) — the
  // biomarkers resource backing this panel has no such fields. Match by id,
  // which is shared between the two resources. Null when the biomarker isn't
  // part of the client's report (e.g. recorded manually outside it) or when
  // the report ships no factors for it.
  const reportFactors = useMemo(() => {
    const id = userBiomarkerData?.biomarkerID;
    if (!id) return null;
    const match: any = [
      ...(dashboard?.bloodbiomarkers || []),
      ...(dashboard?.breathbiomarkers || []),
    ].find((b: any) => b.id === id);
    if (!match) return null;
    return {
      influencedBy: {
        positive: match.influencedBy?.positive || [],
        negative: match.influencedBy?.negative || [],
      },
      affectsThis: match.affectsThis || [],
    };
  }, [dashboard, userBiomarkerData?.biomarkerID]);

  // Reset transient state when another biomarker is opened.
  useEffect(() => {
    setActiveTab(0);
    setEditMode(false);
    setValue("");
    setDate(undefined);
  }, [userBiomarkerData?.biomarkerID]);

  if (!item) return null;

  // Full reading history for the Readings / Levels-over-time lists. The header
  // value comes from the item itself (item.value / item.qvalue / range_* are
  // the current reading); `latest` is only used for the last-recorded date.
  const entries = getAllEntries(item);
  const latest = getLatestEntry(item);
  const recorded = hasRecordedValue(item);
  const badge = recorded ? getBiomarkerBadge(item.scoreCategory, theme) : null;
  const catalogInfo = biomarkerList?.find(
    (b: any) => b.id === item.biomarkerID
  );

  const systemNames = Array.from(
    new Set(
      (item.metrics || [])
        .filter((m) => m !== 0)
        .map(
          (m) =>
            systems?.find((s: any) => s.system === m)?.name ||
            enPrecisionMetric[m]
        )
        .filter(Boolean)
    )
  );

  const kindLabel = enPrecisionTestKind[item.testKind] || "Other";

  const refreshData = async () => {
    // Only await the biomarker reload — that drives this panel. The ReportV2
    // (score) refresh runs in the background, same as the Biomarkers tab.
    await ASGetClientBiomarkerTabDataHandler(item.userID);
    ASGetDataFromPlatform(item.userID, AuthUser.role, true);
  };

  const handleDelete = async (entryId: string) => {
    if (editMode) return;
    try {
      setIsLoading(true);
      await DeleteCostumerBiomarker(entryId, item.biomarkerID, item.userID);
      await refreshData();
    } catch (error: any) {
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: `Error ${error.status}`,
        message: "Failed to delete biomarker value",
      });
      ASSetTypeSnackBar("alert");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (date === undefined) return;
    try {
      setIsLoading(true);
      await UpdateCostumerBiomarker(
        item.biomarkerID,
        item.userID,
        date,
        item.qualitative ? 0 : +value,
        item.qualitative ? value : undefined
      );
      await refreshData();
      setEditMode(false);
      setValue("");
      setDate(undefined);
    } catch (error: any) {
      ASSetOpenSnackBar(true);
      ASSetSnackBarMessage({
        title: `Error ${error.status}`,
        message: "Failed to save biomarker value",
      });
      ASSetTypeSnackBar("alert");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.layout}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.titleRow}>
          <div style={styles.title(theme)}>{item.name}</div>
          <div style={styles.closeIcon} onClick={closePanel}>
            <XIconSvg />
          </div>
        </div>
        <div style={styles.valueRow}>
          <span style={styles.value(theme)}>
            {recorded
              ? formatBiomarkerTabValue(item, item.unit, item.qualitative)
              : `— ${item.unit || ""}`}
          </span>
          {badge && (
            <span style={styles.badge(badge.background, badge.color)}>
              {badge.label}
            </span>
          )}
          {recorded && !item.qualitative && <TrendSparkline item={item} />}
        </div>
        <BiomarkerRangeBar item={item} />
        {systemNames.length > 0 && (
          <div style={styles.systemsRow}>
            <span style={styles.systemsLabel(theme)}>System affected:</span>
            {systemNames.map((name, idx) => (
              <span key={idx} style={styles.systemChip(theme)}>
                {name}
              </span>
            ))}
          </div>
        )}
        <div style={styles.kindRow(theme)}>
          {kindLabel}
          {"  •  "}
          {latest ? formatDisplayDate(latest.dateCreated) : "Not recorded"}
        </div>
      </div>

      {/* Divider between the header block and the tabs (Figma "Line 192" in
          16330:70435). */}
      <div style={styles.headerDivider(theme)} />

      <div style={styles.tabsWrapper(theme)}>
        <SecondaryTabMenuPicker
          tabs={[
            { label: "About", key: 0 },
            { label: "Readings", key: 1 },
          ]}
          selectedTab={activeTab}
          setSelectedTab={(key: number) => {
            if (key === 1 && readingsLocked) {
              ASSetSnackBarMessage({
                title: "Sample data preview",
                message:
                  "Readings can't be opened from the sample preview. Use All biomarkers or Add results to record real values.",
              });
              ASSetTypeSnackBar("alert");
              ASSetOpenSnackBar(true);
              return;
            }
            setActiveTab(key);
          }}
          tabStyle={{ flex: 1, justifyContent: "center" }}
        />
      </div>

      {isLoading ? (
        <div style={styles.loaderWrapper}>
          <LoadingSpinner
            size="small"
            color={theme.colors_ds.NEUTRAL_TEXT_DEFAULT}
          />
        </div>
      ) : activeTab === 0 ? (
        <ScrollableWrapper customPadding="0">
          <div style={styles.content}>
            <AboutTab
              theme={theme}
              item={item}
              entries={entries}
              description={
                typeof catalogInfo?.text === "string"
                  ? catalogInfo.text
                  : undefined
              }
              reportFactors={reportFactors}
            />
          </div>
        </ScrollableWrapper>
      ) : (
        // Readings owns its own scroll region so "Add value" can stay pinned
        // below it — see ReadingsTab.
        <ReadingsTab
          theme={theme}
          item={item}
          entries={entries}
          canEdit={canEdit}
          editMode={editMode}
          setEditMode={setEditMode}
          value={value}
          setValue={setValue}
          date={date}
          setDate={setDate}
          onDelete={handleDelete}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

// --- About tab ---
const AboutTab = ({
  theme,
  item,
  entries,
  description,
  reportFactors,
}: {
  theme: Theme;
  item: BiomarkerItem;
  entries: ReturnType<typeof getAllEntries>;
  description?: string;
  reportFactors: {
    influencedBy: {
      positive: { label: string; description?: string }[];
      negative: { label: string; description?: string }[];
    };
    affectsThis: { label: string; description?: string }[];
  } | null;
}) => {
  // Plain readings plot as dots; range readings ("<2.0") as a hatched span
  // from their bound (see CustomLineChart's ChartRange).
  const chartEntries = getChartEntries(item);

  const badge = getBiomarkerBadge(item.scoreCategory, theme);
  const hasFactors = Boolean(
    reportFactors &&
      (reportFactors.influencedBy.positive.length > 0 ||
        reportFactors.influencedBy.negative.length > 0 ||
        reportFactors.affectsThis.length > 0)
  );
  // The middle score node needs a numeric reading — qualitative biomarkers and
  // ones with no recorded value show the chips connected directly instead.
  const hasNumericScore =
    !item.qualitative &&
    hasRecordedValue(item) &&
    typeof item.value === "number";

  return (
    <>
      {description && description.trim() !== "" ? (
        <div style={styles.description(theme)}>
          {/* Catalog texts carry literal <br> separators — render them as
              paragraph breaks. */}
          {description
            .split(/<br\s*\/?>/i)
            .filter((part) => part.trim() !== "")
            .map((part, idx) => (
              <p key={idx} style={styles.descriptionParagraph}>
                {part.trim()}
              </p>
            ))}
        </div>
      ) : (
        // Most catalog entries ship without a `text` yet (~1660 of 1960 on
        // dev) — show an explicit empty state instead of a blank tab.
        <div style={styles.emptyDescription(theme)}>
          No description available for this biomarker yet.
        </div>
      )}

      {/* Influenced by → score → Affects this. Only the report ships these
          factors, so the block is absent for biomarkers outside it. */}
      {hasFactors && reportFactors && (
        <TimelineGroup
          theme={theme}
          influencedBy={reportFactors.influencedBy}
          affectsItems={{ positive: [], negative: reportFactors.affectsThis }}
          score={item.value ?? 0}
          scoreUnit={item.unit ? ` ${item.unit}` : ""}
          scoreCategory={item.scoreCategory}
          categoryColor={getBiomarkerPointerColor(item.scoreCategory, theme)}
          categoryLabel={badge?.label}
          categoryVariant="biomarkers"
          hideScore={!hasNumericScore}
        />
      )}

      {item.qualitative
        ? entries.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle(theme)}>Levels over time</div>
              <div style={styles.levelsCard(theme)}>
                {entries.map((entry, idx) => {
                  // Each past reading carries the category it scored at its
                  // own date.
                  const badge =
                    entry.scoreCategory !== undefined
                      ? getBiomarkerBadge(entry.scoreCategory, theme)
                      : null;
                  return (
                    <div key={idx} style={styles.levelsRow(theme, idx > 0)}>
                      <div style={styles.levelsValueWrap}>
                        <span style={styles.levelsValue(theme)}>
                          {entry.qvalue}
                        </span>
                        {badge && (
                          <span
                            style={styles.badge(badge.background, badge.color)}
                          >
                            {badge.label}
                          </span>
                        )}
                      </div>
                      <span style={styles.levelsDate(theme)}>
                        {formatDisplayDate(entry.dateCreated)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )
        : chartEntries.length > 0 && (
            <div style={styles.section}>
              <div style={styles.sectionTitle(theme)}>Trend history</div>
              {/* Reference-range segments sit behind the line; each point is
                  colored by the category that reading scored. */}
              <CustomLineChart
                id={item.biomarkerID}
                data={chartEntries.map((e) => {
                  const bounds = getRangeBounds(e);
                  if (bounds) {
                    // The line meets the span at its bound (the midpoint of
                    // a two-sided "a–b"). Category hatch, grey without one.
                    const { lower, upper } = bounds;
                    const hatch = getBiomarkerHatchColors(
                      e.scoreCategory ?? 0,
                      theme
                    );
                    return {
                      value:
                        lower !== null && upper !== null
                          ? (lower + upper) / 2
                          : (upper ?? lower) as number,
                      dateCreated: e.dateCreated,
                      range: { lower, upper, ...hatch },
                      label: formatBiomarkerTabValue(e, item.unit),
                    };
                  }
                  return {
                    value: e.value,
                    dateCreated: e.dateCreated,
                    color:
                      e.scoreCategory !== undefined
                        ? getBiomarkerPointerColor(e.scoreCategory, theme)
                        : undefined,
                  };
                })}
                unit={item.unit}
                bands={
                  hasValidRanges(item)
                    ? item.ranges!.map((r) => ({
                        from: r.min,
                        to: r.max,
                        color: getRangeBandColor(r.category, theme),
                      }))
                    : undefined
                }
              />
            </div>
          )}
    </>
  );
};

// --- Readings tab ---
const ReadingsTab = ({
  theme,
  item,
  entries,
  canEdit,
  editMode,
  setEditMode,
  value,
  setValue,
  date,
  setDate,
  onDelete,
  onSave,
}: {
  theme: Theme;
  item: BiomarkerItem;
  entries: ReturnType<typeof getAllEntries>;
  canEdit: boolean;
  editMode: boolean;
  setEditMode: (v: boolean) => void;
  value: string;
  setValue: (v: string) => void;
  date: string | undefined;
  setDate: (v: string) => void;
  onDelete: (entryId: string) => void;
  onSave: () => void;
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  const saveDisabled =
    date === "" ||
    date === undefined ||
    !isValidDate(new Date(date)) ||
    value === "";

  // The add-value form opens at the top of the list, which may be scrolled out
  // of view — bring it back into view as it opens. This has to run AFTER the
  // form is committed to the DOM: scrolling in the click handler instead lands
  // before the insert, and Chrome's scroll anchoring then pushes the list back
  // down by the form's height (observed: scrollTop 218 → 296 instead of 0).
  useEffect(() => {
    if (editMode) listRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [editMode]);

  return (
    <div style={styles.readingsLayout}>
      <div style={styles.sectionTitle(theme)}>Recorded values</div>

      {/* Only the value cards scroll; the "Add value" button below stays put. */}
      <ScrollableWrapper ref={listRef} customPadding="0">
        <div style={styles.readingsList}>
          {editMode && (
            <AddNewBiomarkerWidget
              unit={item.unit}
              setEditMode={setEditMode}
              value={value}
              setValue={setValue}
              date={date}
              setDate={setDate}
              qualitative={item.qualitative}
              qoptions={item.qoptions}
              onSave={onSave}
              saveDisabled={saveDisabled}
            />
          )}

          {entries.length > 0
            ? entries.map((entry, idx) => (
                <div key={idx} style={styles.readingCard(theme)}>
                  <div style={{ flex: 1 }}>
                    <div style={styles.readingLabel(theme)}>Value</div>
                    <div style={styles.readingValue(theme)}>
                      {formatBiomarkerTabValue(
                        entry,
                        item.unit,
                        item.qualitative
                      )}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={styles.readingLabel(theme)}>Date</div>
                    <div style={styles.readingValue(theme)}>
                      {formatDisplayDate(entry.dateCreated)}
                    </div>
                  </div>
                  {canEdit && (
                    <RoundedIconContainer
                      isDisabled={editMode}
                      handleClick={() => onDelete(entry.id)}
                    >
                      <BinSvg
                        color={
                          editMode
                            ? theme.colors_ds.NEUTRAL_ICON_WEAK
                            : theme.colors_ds.PRIMARY_ICON_MAIN
                        }
                      />
                    </RoundedIconContainer>
                  )}
                </div>
              ))
            : !editMode && (
                <div style={styles.emptyReadings(theme)}>
                  {canEdit
                    ? "No readings yet. Add the first value below."
                    : "No readings recorded yet."}
                </div>
              )}
        </div>
      </ScrollableWrapper>

      {canEdit && !editMode && (
        <div style={styles.readingsFooter(theme)}>
          <ActionButton
            label="Add value"
            icon={<PlusSvg />}
            onClick={() => setEditMode(true)}
            sx={{ width: "100%", justifyContent: "center" }}
            centerContent
          />
        </div>
      )}
    </div>
  );
};

export default UserBiomarkerPanel;

const styles = {
  layout: {
    display: "flex",
    flexDirection: "column" as const,
    height: "100%",
    minHeight: 0,
  },
  header: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "10px",
    paddingBottom: "12px",
  },
  titleRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "16px",
  },
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "22px",
    lineHeight: "30px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    flex: 1,
  }),
  closeIcon: {
    cursor: "pointer",
    marginTop: "6px",
  },
  valueRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  value: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "16px",
    lineHeight: "22px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  badge: (background: string, color: string) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
    padding: "2px 8px",
    borderRadius: "4px",
    backgroundColor: background,
    color,
    whiteSpace: "nowrap" as const,
  }),
  systemsRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexWrap: "wrap" as const,
  },
  systemsLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  systemChip: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "16px",
    padding: "2px 8px",
    borderRadius: "4px",
    backgroundColor: theme.colorTokens.N[30],
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  kindRow: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    whiteSpace: "pre" as const,
  }),
  headerDivider: (theme: Theme) => ({
    height: "1px",
    flexShrink: 0,
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
    marginBottom: "20px",
  }),
  tabsWrapper: (theme: Theme) => ({
    marginBottom: "16px",
  }),
  content: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
    paddingBottom: "20px",
  },
  loaderWrapper: {
    display: "flex",
    justifyContent: "center",
    padding: "32px 0",
  },
  // Readings tab: title / scrolling card list / pinned "Add value" footer.
  readingsLayout: {
    display: "flex",
    flexDirection: "column" as const,
    flex: 1,
    minHeight: 0,
    gap: "16px",
  },
  readingsList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
    paddingBottom: "4px",
  },
  readingsFooter: (theme: Theme) => ({
    flexShrink: 0,
    paddingBottom: "20px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  description: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "13px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  descriptionParagraph: {
    margin: "0 0 8px 0",
  },
  section: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
  sectionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  levelsCard: (theme: Theme) => ({
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "4px 16px",
  }),
  levelsRow: (theme: Theme, withDivider: boolean) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 0",
    borderTop: withDivider
      ? `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`
      : "none",
  }),
  levelsValueWrap: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  levelsValue: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "13px",
    lineHeight: "18px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  levelsDate: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  readingCard: (theme: Theme) => ({
    padding: "12px 16px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  }),
  readingLabel: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "10px",
  }),
  readingValue: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
  }),
  emptyDescription: (theme: Theme) => ({
    border: `1px dashed ${theme.colors_ds.NEUTRAL_BORDER_DEFAULT}`,
    borderRadius: "8px",
    padding: "24px 16px",
    textAlign: "center" as const,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  emptyReadings: (theme: Theme) => ({
    border: `1px dashed ${theme.colors_ds.NEUTRAL_BORDER_DEFAULT}`,
    borderRadius: "8px",
    padding: "24px 16px",
    textAlign: "center" as const,
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "16px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
};
